/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { Box, Button, Typography } from '@mui/material';
import { useContext, type ReactNode } from 'react';
import { kebabCase } from 'lodash';

import { HighlightSearch } from '../../HighlightSearch/HighlightSearch';
import { GTMContext } from '../../../contexts/GTMContext';
import { GTM_EVENT_CONTINUE, GTM_EVENT_START_OVER } from '../../../constants/GTMEventNames';
import { classNames } from '../../../utils/classNames/classNames';

import './PresetDetailsImages.css';

type PresetDetailsProps = {
	onSelect: () => void;
	onContinue: () => void;
	name: string;
	description: string;
	isActive: boolean;
	isModified: boolean;
};

export function PresetDetails( {
	onSelect,
	onContinue,
	name,
	description,
	isActive,
	isModified
}: PresetDetailsProps ): ReactNode {
	const { triggerGTMEvent } = useContext( GTMContext );

	function handlePresetClick() {
		if ( !isActive ) {
			onSelect();
		}
	}

	function handleStartOver() {
		onSelect();

		triggerGTMEvent( { event: GTM_EVENT_START_OVER } );
	}

	function handleContinue() {
		onContinue();

		triggerGTMEvent( { event: GTM_EVENT_CONTINUE } );
	}

	return (
		<Box
			data-testid={ 'preset-details-box-' + name }
			className='preset-details'
			onClick={ handlePresetClick }
			sx={ {
				position: 'relative',
				padding: '8px',
				borderRadius: '4px',
				border: '1px solid',
				borderColor: 'secondary.100',
				cursor: isActive ? 'default' : 'pointer',
				gap: '4px',
				display: 'flex',
				flexFlow: 'column',
				'&:hover': {
					boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.15)'
				},
				...( isActive && {
					borderColor: 'primary.main',
					boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.15)'
				} )
			} }
		>
			<div
				data-testid='preset-details-image'
				className={ classNames( {
					'preset-image': true,
					'preset-image--active': isActive,
					[ `preset-image_${ kebabCase( name ) }` ]: true
				} ) }
				style={ { width: '100%' } }
			>
			</div>
			<Typography fontSize={ '14px' } fontWeight={ 700 } lineHeight={ '22px' } color={ 'text.primary' }>
				<HighlightSearch>
					{ name }
				</HighlightSearch>
			</Typography>
			<Typography fontSize={ '12px' } fontWeight={ 400 } lineHeight={ '20px' } color={ 'text.secondary' }>
				<HighlightSearch>
					{ description }
				</HighlightSearch>
			</Typography>
			{ isActive && (
				<div>
					<Box sx={ { display: 'flex', justifyContent: 'right', gap: '8px', paddingTop: '8px', marginTop: 'auto' } }>
						{ isModified && (
							<Button
								sx={ { fontWeight: '400', fontSize: '12px', padding: '2px 8px', minWidth: 'unset' } }
								variant='text'
								onClick={ handleStartOver }
							>
								Start over
							</Button>
						) }
						<Button
							sx={ { fontWeight: '400', fontSize: '12px', padding: '2px 8px', minWidth: 'unset' } }
							variant='contained'
							onClick={ handleContinue }
						>
							Continue
						</Button>
					</Box>
				</div>
			) }
		</Box>
	);
}
